import React, { Fragment, useEffect, useMemo, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import {
  ChevronUpIcon,
  ChevronDownIcon,
  XMarkIcon,
  EnvelopeIcon,
  PhoneIcon,
  UserCircleIcon,
  BuildingOffice2Icon,
  IdentificationIcon,
  ClockIcon,
  PaperAirplaneIcon,
  LockClosedIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  TagIcon,
  ChatBubbleLeftRightIcon,
} from "@heroicons/react/24/outline";
import ConnectGmailButton from "../../leadsComponents/ConnectGmailButton";
import { INQUIRY_TYPES, TYPE_TONE } from "../constants";
import {
  classNames,
  firstResponseMinutes,
  formatDateTime,
  formatDuration,
  formatRelative,
  statusOf,
  urgencyFor,
  urgencyTone,
} from "./utils";
import InquiryReplyComposer from "./InquiryReplyComposer";
import ConversationThread from "./ConversationThread";

// Full rebuild of the inquiry modal. The old version hid the thread behind
// a collapsible card, rendered plain-text responses with pre-wrap (so HTML
// tags showed as literal text), and scattered critical facts across three
// columns. The new layout follows an Intercom / HubSpot style workflow:
//
//   1. Header  — who + status + quick actions
//   2. Left    — guest & property context, internal notes
//   3. Center  — unified conversation thread (sanitized HTML)
//   4. Right   — quick actions & metadata
//   5. Bottom  — reply composer (collapsible drawer)
//
// All HTML content is sanitized via the shared `SafeHtml` helper (see
// `src/utils/sanitizeHtml.js`). The thread itself is built by
// `ConversationThread.buildThreadEvents()` which merges messages, original
// inquiry, responses, and lifecycle events into one chronological view.
export default function InquiryReplyModal({
  open,
  inquiry,
  loading,
  adminInfo,
  gmailConnected,
  onConnectGmail,
  onClose,
  onPatchInquiry,
  onSendReply,
  onNext,
  onPrevious,
  onManageTemplates,
}) {
  const [notesDraft, setNotesDraft] = useState("");
  const [savingNotes, setSavingNotes] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState("");
  const [replyOpen, setReplyOpen] = useState(false);

  useEffect(() => {
    setNotesDraft(inquiry?.notes || "");
    setSendError("");
    // Open the reply drawer by default whenever the inquiry still needs a
    // response. Closed / already-replied inquiries start with it collapsed
    // so operators are reading the thread first, not typing.
    setReplyOpen(Boolean(inquiry && !inquiry.replied && !inquiry.closed));
  }, [inquiry?._id, inquiry?.notes, inquiry?.replied, inquiry?.closed]);

  const responderName = `${adminInfo?.firstName || ""} ${adminInfo?.lastName || ""}`.trim();
  const firstRespMin = useMemo(() => firstResponseMinutes(inquiry), [inquiry]);

  const saveNotes = async () => {
    if (!inquiry?._id) return;
    setSavingNotes(true);
    try {
      await onPatchInquiry?.(inquiry._id, { notes: notesDraft });
    } finally {
      setSavingNotes(false);
    }
  };

  const toggleClosed = async () => {
    if (!inquiry?._id) return;
    await onPatchInquiry?.(inquiry._id, { closed: !inquiry.closed });
  };

  const markReplied = async () => {
    if (!inquiry?._id) return;
    await onPatchInquiry?.(inquiry._id, { replied: true });
  };

  const changeType = async (next) => {
    if (!inquiry?._id) return;
    await onPatchInquiry?.(inquiry._id, { type: next });
  };

  const handleSendReply = async (payload) => {
    if (!inquiry?._id) return;
    setSending(true);
    setSendError("");
    try {
      await onSendReply?.({
        id: inquiry._id,
        responder: responderName,
        ...payload,
      });
      onClose?.();
    } catch (err) {
      setSendError(err?.response?.data?.error || err?.message || "Failed to send reply");
    } finally {
      setSending(false);
    }
  };

  return (
    <Transition.Root show={!!open} as={Fragment}>
      <Dialog as="div" className="relative z-40" onClose={() => onClose?.()}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-zinc-900/50 backdrop-blur-[2px]" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-stretch justify-center p-3 sm:p-6">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 translate-y-2 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-2 sm:scale-95"
            >
              <Dialog.Panel className="relative flex h-[calc(100vh-3rem)] w-full max-w-[1180px] flex-col overflow-hidden rounded-2xl bg-white shadow-2xl ring-1 ring-zinc-200">
                {!inquiry ? (
                  <div className="flex h-[60vh] items-center justify-center text-sm text-zinc-500">
                    {loading ? "Loading inquiry…" : "No inquiry selected"}
                  </div>
                ) : (
                  <>
                    <ModalHeader
                      inquiry={inquiry}
                      firstRespMin={firstRespMin}
                      onNext={onNext}
                      onPrevious={onPrevious}
                      onClose={onClose}
                      onChangeType={changeType}
                      onMarkReplied={markReplied}
                      onToggleClosed={toggleClosed}
                    />

                    <div className="grid min-h-0 flex-1 grid-cols-1 lg:grid-cols-[minmax(260px,320px)_minmax(0,1fr)]">
                      <ContextRail
                        inquiry={inquiry}
                        notesDraft={notesDraft}
                        setNotesDraft={setNotesDraft}
                        savingNotes={savingNotes}
                        saveNotes={saveNotes}
                      />

                      <section className="flex min-h-0 flex-col border-l border-zinc-200 bg-white">
                        <div className="flex items-center justify-between border-b border-zinc-200 px-5 py-3">
                          <div className="flex items-center gap-2">
                            <ChatBubbleLeftRightIcon className="h-4 w-4 text-zinc-400" />
                            <h3 className="text-xs font-semibold uppercase tracking-wider text-zinc-500">
                              Conversation
                            </h3>
                            <span className="rounded-full bg-zinc-100 px-2 py-0.5 text-[11px] font-semibold text-zinc-600">
                              {(inquiry.messages || []).length + (inquiry.responses || []).length || 1}
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            {onManageTemplates ? (
                              <button
                                type="button"
                                onClick={onManageTemplates}
                                className="rounded-md border border-zinc-200 bg-white px-2.5 py-1 text-[11px] font-medium text-zinc-700 hover:bg-zinc-50"
                              >
                                Templates
                              </button>
                            ) : null}
                            <button
                              type="button"
                              onClick={() => setReplyOpen((v) => !v)}
                              className={classNames(
                                "inline-flex items-center gap-1.5 rounded-md px-2.5 py-1 text-[11px] font-semibold ring-1 transition-colors",
                                replyOpen
                                  ? "bg-zinc-900 text-white ring-zinc-900 hover:bg-zinc-800"
                                  : "bg-indigo-600 text-white ring-indigo-600 hover:bg-indigo-500"
                              )}
                            >
                              <PaperAirplaneIcon className="h-3.5 w-3.5" />
                              {replyOpen ? "Hide reply" : "Reply"}
                            </button>
                          </div>
                        </div>

                        <div className="min-h-0 flex-1 overflow-y-auto bg-zinc-50/50 px-5 py-5">
                          <ConversationThread
                            inquiry={inquiry}
                            emptyHint="No messages yet on this inquiry."
                          />
                        </div>

                        <ReplyDrawer
                          open={replyOpen}
                          onClose={() => setReplyOpen(false)}
                          inquiry={inquiry}
                          adminInfo={adminInfo}
                          gmailConnected={gmailConnected}
                          onConnectGmail={onConnectGmail}
                          sending={sending}
                          sendError={sendError}
                          onSend={handleSendReply}
                        />
                      </section>
                    </div>
                  </>
                )}
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}

function ModalHeader({
  inquiry,
  firstRespMin,
  onNext,
  onPrevious,
  onClose,
  onChangeType,
  onMarkReplied,
  onToggleClosed,
}) {
  const tone = TYPE_TONE[inquiry?.type || "Other"] || TYPE_TONE.Other;
  return (
    <header className="border-b border-zinc-200 bg-white px-5 py-3.5">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <div className="flex min-w-0 flex-wrap items-center gap-2">
            <Dialog.Title className="truncate text-[17px] font-semibold leading-6 text-zinc-900">
              {inquiry.name || "Unnamed contact"}
            </Dialog.Title>
            <StatusBadge inquiry={inquiry} />
            <UrgencyChip inquiry={inquiry} />
            <span
              className={classNames(
                "inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[11px] font-medium ring-1",
                tone.chip
              )}
            >
              <span className={classNames("inline-block h-1.5 w-1.5 rounded-full", tone.dot)} />
              {inquiry.type || "Other"}
            </span>
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-[12px] text-zinc-500">
            {inquiry.email ? (
              <a
                href={`mailto:${inquiry.email}`}
                className="inline-flex items-center gap-1 hover:text-indigo-700 hover:underline"
              >
                <EnvelopeIcon className="h-3.5 w-3.5" />
                {inquiry.email}
              </a>
            ) : null}
            {inquiry.phone ? (
              <a
                href={`tel:${inquiry.phone}`}
                className="inline-flex items-center gap-1 hover:text-indigo-700 hover:underline"
              >
                <PhoneIcon className="h-3.5 w-3.5" />
                {inquiry.phone}
              </a>
            ) : null}
            <span className="inline-flex items-center gap-1">
              <ClockIcon className="h-3.5 w-3.5" />
              <span title={new Date(inquiry.createdAt).toString()}>
                received {formatRelative(inquiry.createdAt)}
              </span>
            </span>
            {firstRespMin != null ? (
              <span className="inline-flex items-center gap-1 text-zinc-500">
                <span className="text-zinc-300">·</span>
                1st reply in {formatDuration(firstRespMin)}
              </span>
            ) : null}
          </div>
        </div>

        <div className="flex shrink-0 items-center gap-1.5">
          <select
            value={inquiry.type || "Other"}
            onChange={(e) => onChangeType(e.target.value)}
            className="rounded-md border border-zinc-200 bg-white px-2 py-1 text-[12px] text-zinc-700 focus:border-indigo-500 focus:outline-none"
            title="Change inquiry type"
          >
            {INQUIRY_TYPES.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
          {!inquiry.replied && !inquiry.closed ? (
            <button
              type="button"
              onClick={onMarkReplied}
              className="inline-flex items-center gap-1 rounded-md border border-indigo-200 bg-indigo-50 px-2 py-1 text-[11px] font-semibold text-indigo-700 hover:bg-indigo-100"
              title="Mark as replied without sending"
            >
              <CheckCircleIcon className="h-3.5 w-3.5" /> Mark replied
            </button>
          ) : null}
          <button
            type="button"
            onClick={onToggleClosed}
            className={classNames(
              "inline-flex items-center gap-1 rounded-md border px-2 py-1 text-[11px] font-semibold",
              inquiry.closed
                ? "border-zinc-200 bg-white text-zinc-700 hover:bg-zinc-50"
                : "border-red-200 bg-red-50 text-red-700 hover:bg-red-100"
            )}
          >
            {inquiry.closed ? (
              <>
                <ArrowPathIcon className="h-3.5 w-3.5" /> Reopen
              </>
            ) : (
              <>
                <LockClosedIcon className="h-3.5 w-3.5" /> Close
              </>
            )}
          </button>

          <span className="mx-1 h-5 w-px bg-zinc-200" />

          <button
            type="button"
            onClick={onPrevious}
            title="Previous (k)"
            className="rounded-md border border-zinc-200 bg-white p-1 text-zinc-600 hover:bg-zinc-50"
          >
            <ChevronUpIcon className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={onNext}
            title="Next (j)"
            className="rounded-md border border-zinc-200 bg-white p-1 text-zinc-600 hover:bg-zinc-50"
          >
            <ChevronDownIcon className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={onClose}
            title="Close (Esc)"
            className="rounded-md p-1 text-zinc-500 hover:bg-zinc-100"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>
      </div>
    </header>
  );
}

function ContextRail({ inquiry, notesDraft, setNotesDraft, savingNotes, saveNotes }) {
  return (
    <aside className="flex min-h-0 flex-col overflow-y-auto bg-zinc-50/60">
      <div className="space-y-3 p-4">
        <SectionCard title="Contact" icon={UserCircleIcon}>
          <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1.5 text-[12px]">
            <dt className="text-zinc-500">Name</dt>
            <dd className="font-medium text-zinc-900">{inquiry.name || "—"}</dd>
            <dt className="text-zinc-500">Email</dt>
            <dd className="truncate font-medium text-zinc-900">
              {inquiry.email ? (
                <a className="hover:text-indigo-700 hover:underline" href={`mailto:${inquiry.email}`}>
                  {inquiry.email}
                </a>
              ) : (
                "—"
              )}
            </dd>
            <dt className="text-zinc-500">Phone</dt>
            <dd className="font-medium text-zinc-900">
              {inquiry.phone ? (
                <a className="hover:text-indigo-700 hover:underline" href={`tel:${inquiry.phone}`}>
                  {inquiry.phone}
                </a>
              ) : (
                "—"
              )}
            </dd>
            {inquiry.assignedTo ? (
              <>
                <dt className="text-zinc-500">Assigned</dt>
                <dd className="font-medium text-zinc-900">{inquiry.assignedTo}</dd>
              </>
            ) : null}
          </dl>
        </SectionCard>

        {inquiry.propertyUniqueId || inquiry.guestId || inquiry.source ? (
          <SectionCard title="Context" icon={BuildingOffice2Icon}>
            <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1.5 text-[12px]">
              {inquiry.propertyUniqueId ? (
                <>
                  <dt className="text-zinc-500">Property</dt>
                  <dd className="truncate font-mono text-[11px] text-zinc-800">
                    {inquiry.propertyUniqueId}
                  </dd>
                </>
              ) : null}
              {inquiry.guestId ? (
                <>
                  <dt className="text-zinc-500">Guest ID</dt>
                  <dd className="truncate font-mono text-[11px] text-zinc-800">{inquiry.guestId}</dd>
                </>
              ) : null}
              {inquiry.source ? (
                <>
                  <dt className="text-zinc-500">Source</dt>
                  <dd className="font-medium text-zinc-800">{inquiry.source}</dd>
                </>
              ) : null}
            </dl>
          </SectionCard>
        ) : null}

        <SectionCard title="Timeline" icon={ClockIcon}>
          <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1.5 text-[12px]">
            <dt className="text-zinc-500">Created</dt>
            <dd className="font-medium text-zinc-900" title={new Date(inquiry.createdAt).toString()}>
              {formatDateTime(inquiry.createdAt)}
            </dd>
            {inquiry.respondedAt ? (
              <>
                <dt className="text-zinc-500">First reply</dt>
                <dd className="font-medium text-zinc-900">{formatDateTime(inquiry.respondedAt)}</dd>
              </>
            ) : null}
            {inquiry.updatedAt ? (
              <>
                <dt className="text-zinc-500">Last activity</dt>
                <dd className="font-medium text-zinc-900">{formatRelative(inquiry.updatedAt)}</dd>
              </>
            ) : null}
            {inquiry.threadId ? (
              <>
                <dt className="text-zinc-500">Thread</dt>
                <dd className="truncate font-mono text-[11px] text-zinc-700">{inquiry.threadId}</dd>
              </>
            ) : null}
          </dl>
        </SectionCard>

        <SectionCard
          title="Internal notes"
          icon={IdentificationIcon}
          action={
            <button
              type="button"
              onClick={saveNotes}
              disabled={savingNotes}
              className="rounded-md bg-indigo-600 px-2 py-0.5 text-[11px] font-semibold text-white hover:bg-indigo-500 disabled:bg-zinc-300"
            >
              {savingNotes ? "Saving…" : "Save"}
            </button>
          }
        >
          <textarea
            rows={4}
            value={notesDraft}
            onChange={(e) => setNotesDraft(e.target.value)}
            placeholder="Private context for your team — not sent to the guest."
            className="w-full rounded-lg border border-zinc-200 bg-white px-2.5 py-1.5 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
          />
        </SectionCard>

        <SectionCard title="Tags" icon={TagIcon}>
          <div className="flex flex-wrap gap-1.5">
            <ContextTag>Status: {statusOf(inquiry)}</ContextTag>
            <ContextTag>Type: {inquiry.type || "Other"}</ContextTag>
            {inquiry.replied ? <ContextTag tone="indigo">Replied</ContextTag> : null}
            {inquiry.closed ? <ContextTag tone="zinc">Closed</ContextTag> : null}
            {!inquiry.replied && !inquiry.closed ? (
              <ContextTag tone="amber">Awaiting reply</ContextTag>
            ) : null}
          </div>
        </SectionCard>
      </div>
    </aside>
  );
}

function ReplyDrawer({
  open,
  onClose,
  inquiry,
  adminInfo,
  gmailConnected,
  onConnectGmail,
  sending,
  sendError,
  onSend,
}) {
  if (!open) return null;
  return (
    <div className="flex max-h-[55vh] min-h-[260px] flex-col overflow-hidden border-t border-zinc-200 bg-white shadow-[0_-2px_12px_rgba(0,0,0,0.04)]">
      <div className="flex items-center justify-between border-b border-zinc-100 bg-zinc-50 px-5 py-2">
        <div className="flex items-center gap-2">
          <PaperAirplaneIcon className="h-3.5 w-3.5 text-indigo-500" />
          <h4 className="text-[11px] font-semibold uppercase tracking-wider text-zinc-600">
            Reply to {inquiry.name || inquiry.email || "guest"}
          </h4>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="rounded-md p-1 text-zinc-500 hover:bg-zinc-100"
          aria-label="Close reply"
        >
          <XMarkIcon className="h-4 w-4" />
        </button>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto p-4">
        {gmailConnected ? (
          <InquiryReplyComposer
            inquiry={inquiry}
            adminInfo={adminInfo}
            sending={sending}
            sendError={sendError}
            onSend={onSend}
          />
        ) : (
          <div className="space-y-2">
            <p className="text-sm text-zinc-600">
              Connect Gmail to send a reply from your mailbox.
            </p>
            {onConnectGmail ? (
              <button
                onClick={onConnectGmail}
                className="w-full rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm font-semibold text-amber-900 hover:bg-amber-100"
              >
                Connect Gmail
              </button>
            ) : (
              <ConnectGmailButton />
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function SectionCard({ title, icon: Icon, action, children }) {
  return (
    <section className="rounded-xl border border-zinc-200 bg-white shadow-sm">
      <header className="flex items-center justify-between border-b border-zinc-100 px-3 py-2">
        <div className="flex items-center gap-1.5">
          {Icon ? <Icon className="h-3.5 w-3.5 text-zinc-400" /> : null}
          <h4 className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500">
            {title}
          </h4>
        </div>
        {action}
      </header>
      <div className="p-3">{children}</div>
    </section>
  );
}

function ContextTag({ children, tone = "zinc" }) {
  const map = {
    zinc: "bg-zinc-100 text-zinc-700 ring-zinc-200",
    indigo: "bg-indigo-50 text-indigo-700 ring-indigo-200",
    amber: "bg-amber-50 text-amber-800 ring-amber-200",
    emerald: "bg-emerald-50 text-emerald-700 ring-emerald-200",
  };
  return (
    <span
      className={classNames(
        "inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium ring-1",
        map[tone]
      )}
    >
      {children}
    </span>
  );
}

function StatusBadge({ inquiry }) {
  const status = statusOf(inquiry);
  const map = {
    new: { label: "New", cls: "bg-emerald-50 text-emerald-700 ring-emerald-200" },
    replied: { label: "Replied", cls: "bg-indigo-50 text-indigo-700 ring-indigo-200" },
    closed: { label: "Closed", cls: "bg-zinc-100 text-zinc-600 ring-zinc-200" },
  };
  const meta = map[status];
  return (
    <span
      className={classNames(
        "inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ring-1",
        meta.cls
      )}
    >
      {meta.label}
    </span>
  );
}

function UrgencyChip({ inquiry }) {
  const id = urgencyFor(inquiry);
  if (!id) return null;
  const tone = urgencyTone(id);
  const label = { overdue: "Overdue", stale: "Stale", fresh: "Fresh", new: "Just now" }[id];
  return (
    <span
      className={classNames(
        "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ring-1",
        tone.chip
      )}
    >
      <span className={classNames("inline-block h-1.5 w-1.5 rounded-full", tone.dot)} />
      {label}
    </span>
  );
}
